import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShieldCheck, ShieldX } from "lucide-react";
import api from "../lib/api";
import { PageHero, Section } from "../components/PageKit";
import { usePageMeta } from "../lib/seo";

const TYPE_LABEL = { application: "Application", beneficiary: "Beneficiary registration", donor: "Donor reference", family_food: "Family Food application", project: "Project", supported_project: "Supported project", impact_certificate: "Impact certificate", community_need_report: "Community need report", confidential_report: "Confidential report", volunteer: "Volunteer membership", volunteer_application: "Volunteer application (pending)" };

export default function Verify() {
  usePageMeta("Verify a DKF Reference", "Check whether an application, project, partnership, certificate or fundraising reference issued by Dutsen Kauna Foundation is genuine.");
  const [sp] = useSearchParams();
  const [serial, setSerial] = useState(sp.get("serial") || "");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.get(`/verify-reference?serial=${encodeURIComponent(serial.trim())}`); setRes(data); }
    catch (err) { setRes({ valid: false, message: err.response?.data?.detail || "Could not verify reference." }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="verify-page">
      <PageHero kicker="Public verification centre" title="Verify a DKF Reference" text="Enter any application, beneficiary, donor, project, certificate or report number. We return only limited public information — never private applicant details." />
      <Section className="bg-[#f8f4e8]">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={check} className="bg-white rounded-2xl p-8 border border-black/5 flex flex-col sm:flex-row gap-3" data-testid="verify-form">
            <input value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())} placeholder="e.g. DKF-PRJ-2026-000184" required className="flex-1 px-4 py-3 border border-black/15 rounded-md font-mono focus:outline-none focus:border-[#d6aa43]" data-testid="verify-input" />
            <button type="submit" disabled={busy} className="dk-btn-primary justify-center disabled:opacity-60" data-testid="verify-submit"><Search size={16} /> {busy ? "Checking…" : "Verify"}</button>
          </form>
          {res && (
            <div className={`mt-6 rounded-2xl p-8 border ${res.valid ? "bg-white border-green-200" : "bg-white border-red-200"}`} data-testid={res.valid ? "verify-valid" : "verify-invalid"}>
              {res.valid ? (
                <>
                  <div className="flex items-center gap-2 text-green-700 font-semibold mb-4"><ShieldCheck /> Reference is genuine</div>
                  <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div><dt className="text-[#0a0d12]/60">Reference</dt><dd className="font-mono font-bold" data-testid="verify-serial">{serial.trim()}</dd></div>
                    <div><dt className="text-[#0a0d12]/60">Category</dt><dd className="font-semibold" data-testid="verify-type">{TYPE_LABEL[res.type] || res.type}</dd></div>
                    <div><dt className="text-[#0a0d12]/60">Status</dt><dd className="font-semibold capitalize" data-testid="verify-status">{String(res.status).replace(/_/g, " ")}</dd></div>
                    <div><dt className="text-[#0a0d12]/60">Date created</dt><dd className="font-semibold">{res.date_registered ? new Date(res.date_registered).toLocaleDateString() : "—"}</dd></div>
                  </dl>
                </>
              ) : (
                <div className="flex items-center gap-2 text-red-700 font-semibold"><ShieldX /> {res.message || "Reference not found."}</div>
              )}
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
